import { Component } from '@theme/component';

/** @typedef {typeof globalThis} Window */

/**
 * ProductTitle
 * - Responsive, multi-line truncation (same behavior as your original)
 * - Live variant-aware title: Base – Option1 / Option2 …
 *
 * Usage in Liquid (PDP):
 * <product-title
 *   class="product-title"
 *   data-base="{{ product.title | escape }}"
 *   data-option-names='{{ product.options | json | escape }}'
 *   data-variants='{{ product.variants | json | escape }}'
 *   data-include-options='["Size","Color","Colour"]'  <!-- optional; defaults to all -->
 * >
 *   <span class="title-text">{{ product.title }}</span>
 * </product-title>
 */
class ProductTitle extends Component {
  constructor() {
    super();
    /** @type {ResizeObserver | undefined} */
    this.resizeObserver = undefined;

    // Bound handlers for clean add/remove
    this._onVariantChangeA = this._onVariantChangeA.bind(this);
    this._onVariantChangeB = this._onVariantChangeB.bind(this);
    this._onChangeFallback = this._onChangeFallback.bind(this);
    this._onWindowResize = this._onWindowResize.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();

    // Parse attributes
    this.baseTitle =
      this.getAttribute('data-base') ||
      (this.textRef()?.textContent?.trim() || '');

    this.optionNames = this._safeJSON(this.getAttribute('data-option-names')) || [];
    this.includeOptionNames =
      this._safeJSON(this.getAttribute('data-include-options')) || this.optionNames;

    this.variants =
      this._safeJSON(this.getAttribute('data-variants')) ||
      (Array.isArray(window.__PRODUCT_VARIANTS__) ? window.__PRODUCT_VARIANTS__ : []);

    // Init truncation
    this.#initializeTruncation();

    // Wire variant events
    document.addEventListener('variant:change', this._onVariantChangeA);
    document.addEventListener('variantChange', this._onVariantChangeB);
    document.addEventListener('change', this._onChangeFallback);

    // First paint
    this.updateTitle();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.resizeObserver) this.resizeObserver.disconnect();

    document.removeEventListener('variant:change', this._onVariantChangeA);
    document.removeEventListener('variantChange', this._onVariantChangeB);
    document.removeEventListener('change', this._onChangeFallback);

    window.removeEventListener('resize', this._onWindowResize);
  }

  /** ---------- Utilities ---------- */

  /**
   * @returns {HTMLElement|undefined}
   */
  textRef() {
    return this.refs?.text || this.querySelector('.title-text') || this;
  }

  _safeJSON(raw) {
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { return null; }
  }

  /** ---------- Variant helpers ---------- */

  /**
   * @returns {any|null} current Shopify variant object or null
   */
  getSelectedVariant() {
    // 1) Hidden variant id in form (most themes)
    const idInput = document.querySelector('form[action*="/cart/add"] [name="id"]');
    if (idInput && idInput.value && Array.isArray(this.variants)) {
      const vid = String(idInput.value);
      const found = this.variants.find(v => String(v.id) === vid);
      if (found) return found;
    }
    // 2) Theme/global
    if (window.__CURRENT_VARIANT__) return window.__CURRENT_VARIANT__;
    return null;
  }

  composeSuffix(variant) {
    if (!variant || !this.optionNames?.length) return '';
    const parts = [];
    this.optionNames.forEach((name, idx) => {
      if (!this.includeOptionNames.includes(name)) return;
      const val = variant['option' + (idx + 1)];
      if (val) parts.push(val);
    });
    return parts.length ? (' – ' + parts.join(' / ')) : '';
  }

  setTitleText(text) {
    const el = this.textRef();
    if (!el) return;
    if (el.textContent !== text) {
      el.textContent = text;
      // Re-apply truncation when content changes
      this.#calculateTruncation();
    }
  }

  updateTitle(variant) {
    const v = variant || this.getSelectedVariant();
    const next = (this.baseTitle || '') + this.composeSuffix(v);
    this.setTitleText(next);
    // Optional: mirror to tab title (UX only; not SEO)
    // document.title = next;
  }

  /** ---------- Event handlers ---------- */

  _onVariantChangeA(e) {
    if (e?.detail?.variant) window.__CURRENT_VARIANT__ = e.detail.variant;
    this.updateTitle(e?.detail?.variant);
  }

  _onVariantChangeB(e) {
    if (e?.detail?.variant) window.__CURRENT_VARIANT__ = e.detail.variant;
    this.updateTitle(e?.detail?.variant);
  }

  _onChangeFallback(e) {
    if (
      e.target.matches(
        'form[action*="/cart/add"] [name="id"], [name^="options"], [data-option-selector], .product-form__input input, .product-form__input select'
      )
    ) {
      // Let theme scripts sync the hidden id first
      setTimeout(() => this.updateTitle(), 0);
    }
  }

  /** ---------- Truncation (original behavior, with small guards) ---------- */

  #initializeTruncation() {
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.#calculateTruncation());
      this.resizeObserver.observe(this);
      this.#calculateTruncation();
    } else {
      window.addEventListener('resize', this._onWindowResize);
      this.#calculateTruncation();
    }
  }

  #calculateTruncation() {
    const textElement = this.textRef();
    if (!textElement || !textElement.textContent) return;

    const containerHeight = this.clientHeight || this.offsetHeight;
    if (!containerHeight) return;

    const computedStyle = window.getComputedStyle(this);
    const lineHeight = parseFloat(computedStyle.lineHeight) || 20; // fallback
    const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
    const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;

    const availableHeight = containerHeight - paddingTop - paddingBottom;
    const maxLines = Math.max(1, Math.floor(availableHeight / lineHeight));

    textElement.style.display = '-webkit-box';
    textElement.style.webkitBoxOrient = 'vertical';
    textElement.style.overflow = 'hidden';
    textElement.style.textOverflow = 'ellipsis';
    textElement.style.webkitLineClamp = String(maxLines);
  }

  _onWindowResize() {
    this.#calculateTruncation();
  }
}

if (!customElements.get('product-title')) {
  customElements.define('product-title', ProductTitle);
}

export default ProductTitle;
